import { ServerConfigContext } from '../context/ServerConfigContext';

/**
 * 게시판 API 서비스 클래스
 * Swagger 문서 기반 Stock Board API 연동
 * ServerConfigContext와 연동하여 동적 서버 설정 사용
 */
class BoardApiService {
  constructor() {
    this.serverId = 'board-api-server';
    this.serverConfigContext = null;
    this.swaggerDoc = null;
  }

  // ServerConfigContext 설정
  setServerContext(context) {
    console.log('🔧 [boardApiService] ServerConfigContext 설정:', context);
    this.serverConfigContext = context;
  }

  // 현재 설정된 서버 정보 조회
  getServerConfig() {
    console.log('🔍 [boardApiService] getServerConfig 호출:', {
      hasContext: !!this.serverConfigContext,
      serverId: this.serverId,
      contextType: typeof this.serverConfigContext
    });
    
    if (!this.serverConfigContext) {
      throw new Error('ServerConfigContext가 설정되지 않았습니다.');
    }
    
    const server = this.serverConfigContext.getServer(this.serverId);
    console.log('🔍 [boardApiService] getServer 결과:', server);
    
    if (!server) {
      throw new Error(`서버 설정을 찾을 수 없습니다: ${this.serverId}`);
    }
    
    if (!server.isActive) {
      throw new Error(`서버가 비활성화되어 있습니다: ${this.serverId}`);
    }
    
    return server;
  }

  // 동적 baseUrl과 timeout 조회 (통합 프록시 패턴)
  get baseUrl() {
    // 개발/프로덕션 모두 동일한 프록시 패턴 사용
    return '/board-api/api/v1';
  }

  get timeout() {
    try {
      const server = this.getServerConfig();
      return server.apiTimeout || 10000;
    } catch (error) {
      return 10000;
    }
  }

  // Swagger 문서 로드 (프록시 사용)
  async loadSwaggerDoc() {
    try {
      // 개발 환경에서는 프록시 사용
      let swaggerUrl;
      if (process.env.NODE_ENV === 'development') {
        swaggerUrl = `/api/${this.serverId}/swagger/doc.json`;
      } else {
        // 프로덕션 환경에서는 서버 설정 사용
        const server = this.getServerConfig();
        swaggerUrl = `${server.baseUrl}/swagger/doc.json`;
      }
      
      console.log('📋 Swagger 문서 로드 시도:', swaggerUrl);
      
      const response = await fetch(swaggerUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        timeout: this.timeout
      });
      
      if (!response.ok) {
        throw new Error(`Swagger 문서 로드 실패 (${response.status}): ${response.statusText}`);
      }
      
      this.swaggerDoc = await response.json();
      console.log('✅ Swagger 문서 로드 완료:', this.swaggerDoc.info?.title || 'Unknown API');
      
      return {
        success: true,
        data: this.swaggerDoc,
        message: 'Swagger 문서가 성공적으로 로드되었습니다.'
      };
      
    } catch (error) {
      console.error('❌ Swagger 문서 로드 실패:', error);
      return {
        success: false,
        error: error.name || 'SWAGGER_LOAD_ERROR',
        message: error.message || 'Swagger 문서 로드 중 오류가 발생했습니다.'
      };
    }
  }

  // Swagger 문서에서 API 스키마 정보 조회
  getApiSchema(path, method) {
    if (!this.swaggerDoc || !this.swaggerDoc.paths) {
      return null;
    }
    
    const pathSchema = this.swaggerDoc.paths[path];
    if (!pathSchema) return null;
    
    const methodSchema = pathSchema[method.toLowerCase()];
    return methodSchema || null;
  }

  // API 호출 기본 메서드
  async apiRequest(endpoint, options = {}) {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      
      const defaultOptions = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        },
        timeout: this.timeout,
        ...options
      };

      console.log(`🌐 API 요청: ${defaultOptions.method} ${url}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);
      
      const response = await fetch(url, {
        ...defaultOptions,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`API 오류 (${response.status}): ${errorData.message || response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`✅ API 응답 성공: ${endpoint}`);
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'API 호출 성공'
      };
      
    } catch (error) {
      console.error(`❌ API 요청 실패: ${endpoint}`, error);
      
      if (error.name === 'AbortError') {
        return {
          success: false,
          error: 'REQUEST_TIMEOUT',
          message: `API 요청 시간 초과 (${this.timeout}ms)`
        };
      }
      
      return {
        success: false,
        error: error.name || 'API_ERROR',
        message: error.message || 'API 요청 중 오류가 발생했습니다.'
      };
    }
  }

  // 헬스체크
  async healthCheck() {
    return await this.apiRequest('/health');
  }

  // 게시판 목록 조회
  async getBoards(category = null) {
    const params = new URLSearchParams();
    if (category) {
      params.append('category', category);
    }
    
    const endpoint = `/boards${params.toString() ? `?${params.toString()}` : ''}`;
    return await this.apiRequest(endpoint);
  }

  // 특정 게시판 정보 조회
  async getBoard(boardId) {
    return await this.apiRequest(`/boards/${boardId}`);
  }

  // 게시판 생성 (관리자 전용)
  async createBoard(boardData) {
    return await this.apiRequest('/boards', {
      method: 'POST',
      body: JSON.stringify(boardData)
    });
  }

  // 게시글 목록 조회
  async getPosts(boardId, options = {}) {
    const {
      page = 1,
      limit = 10,
      sort = 'created_at',
      order = 'desc',
      search = null,
      is_solved = null
    } = options;

    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    params.append('sort', sort);
    params.append('order', order);
    
    if (search) params.append('search', search);
    if (is_solved !== null) params.append('is_solved', is_solved.toString());

    const endpoint = `/boards/${boardId}/posts?${params.toString()}`;
    return await this.apiRequest(endpoint);
  }

  // 게시글 작성
  async createPost(boardId, postData) {
    return await this.apiRequest(`/boards/${boardId}/posts`, {
      method: 'POST',
      body: JSON.stringify(postData)
    });
  }

  // 게시글 상세 조회
  async getPost(postId) {
    return await this.apiRequest(`/posts/${postId}`);
  }

  // 게시글 수정
  async updatePost(postId, postData) {
    return await this.apiRequest(`/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(postData)
    });
  }

  // 게시글 삭제
  async deletePost(postId) {
    return await this.apiRequest(`/posts/${postId}`, {
      method: 'DELETE'
    });
  }

  // 댓글 목록 조회
  async getComments(postId, options = {}) {
    const { page = 1, limit = 20 } = options;
    
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const endpoint = `/posts/${postId}/comments?${params.toString()}`;
    return await this.apiRequest(endpoint);
  }

  // 댓글 작성
  async createComment(postId, commentData) {
    return await this.apiRequest(`/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify(commentData)
    });
  }

  // 댓글 수정
  async updateComment(commentId, commentData) {
    return await this.apiRequest(`/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify(commentData)
    });
  }

  // 댓글 삭제
  async deleteComment(commentId) {
    return await this.apiRequest(`/comments/${commentId}`, {
      method: 'DELETE'
    });
  }

  // 게시글 좋아요/추천
  async likePost(postId) {
    return await this.apiRequest(`/posts/${postId}/like`, {
      method: 'POST'
    });
  }

  // 댓글 좋아요/추천
  async likeComment(commentId) {
    return await this.apiRequest(`/comments/${commentId}/like`, {
      method: 'POST'
    });
  }

  // 게시글 조회수 증가
  async increasePostViews(postId) {
    return await this.apiRequest(`/posts/${postId}/views`, {
      method: 'POST'
    });
  }

  // 검색 (전체 게시판 대상)
  async searchPosts(query, options = {}) {
    const {
      page = 1,
      limit = 10,
      sort = 'created_at',
      order = 'desc',
      boardId = null
    } = options;

    const params = new URLSearchParams();
    params.append('q', query);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    params.append('sort', sort);
    params.append('order', order);
    
    if (boardId) params.append('board_id', boardId);

    const endpoint = `/search?${params.toString()}`;
    return await this.apiRequest(endpoint);
  }
}

// 게시판 API 서비스 싱글톤 인스턴스
export const boardApiService = new BoardApiService();

// React Hook으로 사용할 수 있는 유틸리티 함수들 (ServerConfigContext 연동)
export const useBoardApi = (serverConfigContext) => {
  // ServerConfigContext 설정
  console.log('🔧 [useBoardApi] Hook 호출:', {
    hasServerConfigContext: !!serverConfigContext,
    currentContext: !!boardApiService.serverConfigContext
  });
  
  if (serverConfigContext && !boardApiService.serverConfigContext) {
    console.log('🔧 [useBoardApi] ServerConfigContext 설정 중...');
    boardApiService.setServerContext(serverConfigContext);
  }

  return {
    // 서버 설정 관련
    loadSwaggerDoc: () => boardApiService.loadSwaggerDoc(),
    getApiSchema: (path, method) => boardApiService.getApiSchema(path, method),
    
    // 게시판 관련
    getBoards: (category) => boardApiService.getBoards(category),
    getBoard: (boardId) => boardApiService.getBoard(boardId),
    createBoard: (boardData) => boardApiService.createBoard(boardData),
    
    // 게시글 관련
    getPosts: (boardId, options) => boardApiService.getPosts(boardId, options),
    createPost: (boardId, postData) => boardApiService.createPost(boardId, postData),
    getPost: (postId) => boardApiService.getPost(postId),
    updatePost: (postId, postData) => boardApiService.updatePost(postId, postData),
    deletePost: (postId) => boardApiService.deletePost(postId),
    increasePostViews: (postId) => boardApiService.increasePostViews(postId),
    likePost: (postId) => boardApiService.likePost(postId),
    
    // 댓글 관련
    getComments: (postId, options) => boardApiService.getComments(postId, options),
    createComment: (postId, commentData) => boardApiService.createComment(postId, commentData),
    updateComment: (commentId, commentData) => boardApiService.updateComment(commentId, commentData),
    deleteComment: (commentId) => boardApiService.deleteComment(commentId),
    likeComment: (commentId) => boardApiService.likeComment(commentId),
    
    // 유틸리티
    searchPosts: (query, options) => boardApiService.searchPosts(query, options),
    healthCheck: () => boardApiService.healthCheck()
  };
};

// 게시판 타입 매핑 (기존 UI와 호환)
export const BOARD_CATEGORIES = {
  general: {
    title: '일반 게시판',
    boards: [
      { id: 'free', name: '자유게시판', icon: '💬' },
      { id: 'qna', name: 'Q&A', icon: '❓' },
      { id: 'notice', name: '공지사항', icon: '📢' }
    ]
  },
  development: {
    title: '개발',
    boards: [
      { id: 'frontend', name: '프론트엔드', icon: '🎨' },
      { id: 'backend', name: '백엔드', icon: '⚙️' },
      { id: 'mobile', name: '모바일', icon: '📱' }
    ]
  },
  hobby: {
    title: '취미',
    boards: [
      { id: 'photo', name: '사진', icon: '📷' },
      { id: 'music', name: '음악', icon: '🎵' },
      { id: 'game', name: '게임', icon: '🎮' }
    ]
  }
};

// 게시글 정렬 옵션
export const POST_SORT_OPTIONS = [
  { value: 'created_at', label: '최신순' },
  { value: 'likes', label: '인기순' },
  { value: 'comments', label: '댓글순' },
  { value: 'views', label: '조회순' }
];

// 페이지네이션 기본 설정
export const PAGINATION_DEFAULTS = {
  page: 1,
  limit: 10,
  maxPageButtons: 5
};

export default boardApiService;